import {
  Component,
  OnInit,
  viewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DopApiService } from 'src/app/services/api/dop-api.service';
import { ShiftDatePanelComponent } from '../controller-notes/shift-date-panel/shift-date-panel.component';
import { DOPGroup } from 'src/app/model/crm/daily-operating-plan/dop-group';
import { DOPEntry } from 'src/app/model/crm/daily-operating-plan/dop-entry';
import { AOR } from 'src/app/model/locations/aor';
import { AORSubSystem } from 'src/app/model/locations/aor-subsystem';
import { DopEntryFormComponent } from '../../components/dop-entry-form/dop-entry-form.component';
import { sortByProperty, SortDirection } from 'src/app/utils/utils';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { NgClass } from '@angular/common';
import { ProgressSpinner } from '@openng/optimus-ui/progressspinner';
import { ScrollPanel } from '@openng/optimus-ui/scrollpanel';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ContactApiService } from 'src/app/services/api/contact-api.service';
import { AuthService } from 'src/app/services/auth.service';
import { firstValueFrom, forkJoin } from 'rxjs';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { ConfirmationService } from '@openng/optimus-ui/api';
import { LogEntryComponent } from 'src/app/core/components/log-entry.component';
import { TabService } from 'src/app/core/services/tab.service';
@Component({
  selector: 'app-daily-operating-plans-container',
  templateUrl: './daily-operating-plans-container.component.html',
  styleUrls: ['./daily-operating-plans-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ShiftDatePanelComponent,
    ButtonDirective,
    NgClass,
    ProgressSpinner,
    ScrollPanel,
    DopEntryFormComponent,
    TranslatePipe,
    LogEntryComponent,
  ],
})
export class DailyOperatingPlansContainerComponent implements OnInit {
  private _dopApi = inject(DopApiService);
  private _contactApi = inject(ContactApiService);
  private _auth = inject(AuthService);
  private _authApi = inject(AuthApiService);
  private _translateService = inject(TranslateService);
  private _logAndMessage = inject(LogAndMessageService);
  private _confirmationService = inject(ConfirmationService);
  private _tabService = inject(TabService);

  readonly shiftDateSel =
    viewChild.required<ShiftDatePanelComponent>('shiftDateSel');
  readonly dopForm = viewChild.required<DopEntryFormComponent>('dopForm');

  isLoading = false;

  dopGroups: DOPGroup[] = [];
  dopGroupButtons: {
    dopGroup: DOPGroup;
    selected: boolean;
  }[] = [];
  showEntryDialog: boolean = false;

  selectedDOPGroup: any;
  selectedEntry: DOPEntry | null = null;

  dopEntries: DOPEntry[] = [];
  aors: AOR[] = [];

  selectedAor!: AOR;
  selectedAorSubSystem: AORSubSystem | null = null;

  userReportsNames: string[] = [];

  ngOnInit(): void {
    this.isLoading = true;
    this._tabService.updateActiveTabLabel(
      this._translateService.instant('CRM.SCREEN.DAILY_OPERATING_PLANS')
    );
    this._dopApi.getDOPGroups().subscribe({
      next: (dopGroups: DOPGroup[]): any => {
        this.dopGroups = dopGroups;

        this.dopGroups.forEach((group) => {
          this.dopGroupButtons.push({ dopGroup: group, selected: false });
        });

        this.setAORList();

        this.isLoading = false;
      },
      error: (error) => console.error(error),
    });
    this.loadUserReports();
  }

  onDateChange(value: Date) {
    setTimeout(() => {
      let selectedGroup = this.dopGroupButtons.find((g) => g.selected);
      if (selectedGroup) {
        this.loadDOPGroupEntries(selectedGroup);
      }
    }, 50);
  }

  loadDOPGroupEntries(selectedGroup: any, setLoading: boolean = true) {
    if (setLoading) {
      this.isLoading = true;
    }
    this.selectedDOPGroup = selectedGroup;
    this.setAORList();

    // when we change the selector, we want the shift date to be that day,
    // so we set it to start at 6am that day, if we just take the selectors value
    // it starts at midnight, which is the previous day's shift date
    let sd = this.shiftDateSel().value;
    sd.setHours(6);

    this.dopGroupButtons = this.dopGroupButtons.map((g) => {
      g.selected = false;
      return g;
    });

    let matchingGroupIndex = this.dopGroupButtons.findIndex(
      (g) => g.dopGroup.id === this.selectedDOPGroup.dopGroup.id
    );

    if (matchingGroupIndex !== undefined && matchingGroupIndex !== -1) {
      this.dopGroupButtons[matchingGroupIndex].selected = true;
    }

    this._dopApi
      .getDOPEntries(
        this.selectedDOPGroup.dopGroup.id,
        this.getShiftDateString(sd)
      )
      .subscribe(
        (dopEntries: DOPEntry[]): any => {
          this.dopEntries = dopEntries.sort((a, b) =>
            sortByProperty(a, b, 'displayDate', SortDirection.DESCENDING)
          );
          this.isLoading = false;
        },
        (error) => console.error(error)
      );
  }

  getMatchingEntriesForAor(aorId: string) {
    if (this.dopEntries) {
      return this.dopEntries.filter(
        (entry) => entry.aorId === aorId && !entry.aorSubSystemId
      );
    }

    return [];
  }

  getMatchingEntriesForAorSubSystem(aorSubSystemId: string) {
    if (this.dopEntries) {
      return this.dopEntries.filter(
        (entry) => entry.aorSubSystemId === aorSubSystemId
      );
    }

    return [];
  }

  setAORList() {
    this.aors = [];

    if (this.selectedDOPGroup) {
      this.aors.push(...this.selectedDOPGroup.dopGroup.aors);
    }
  }

  addDopEntry(aor: AOR, subSystem: AORSubSystem | null) {
    this.selectedAor = aor;
    this.selectedAorSubSystem = subSystem;
    const aorAndSubSystem: string = subSystem
      ? `${aor.name} - ${subSystem.name}`
      : aor.name;
    this.openEntryForm(aorAndSubSystem);
  }

  onEntryEdit(entry: DOPEntry) {
    this.selectedEntry = entry;
    this.selectedAor = entry.aor!;
    this.selectedAorSubSystem = entry.aorSubSystem!;
    const aorAndSubSystem: string = entry.aorSubSystem
      ? `${entry.aor!.name} - ${entry.aorSubSystem.name}`
      : entry.aor!.name;
    this.openEntryForm(aorAndSubSystem);
  }

  openEntryForm(aorAndSubSystem: string): void {
    this.showEntryDialog = true;
    setTimeout(() => this.dopForm().populateEntryForm(aorAndSubSystem), 50);
  }

  onEntryDialogHidden() {
    this.showEntryDialog = false;
    this.selectedEntry = null;
  }

  onEntryChanged() {
    this.reload();
  }

  reload() {
    let selectedGroup = this.dopGroupButtons.find((g) => g.selected);
    if (selectedGroup) {
      this.loadDOPGroupEntries(selectedGroup, false);
    }
  }

  // Returns a date string in the format YYYYMMDD
  getShiftDateString(sd: Date): string {
    return sd.toISOString().slice(0, 10).replace(/-/g, '');
  }

  areEntriesReadOnly(): boolean {
    const now = new Date();
    const selectedShiftDate = new Date(
      this.shiftDateSel().value.setHours(0, 0, 0, 0)
    );
    const currentShiftDate = new Date(now.setHours(0, 0, 0, 0));

    return selectedShiftDate < currentShiftDate;
  }

  async loadUserReports(): Promise<void> {
    const data = await firstValueFrom(
      this._contactApi.queryForWhoReportsToMe(this._auth.getEmail()!)
    );
    let usernames = data.map((item) =>
      `${item.firstName} ${item.lastName}`.toLowerCase()
    );
    this.userReportsNames = usernames;
  }

  canEditEntry(entry: DOPEntry): boolean {
    if (this.areEntriesReadOnly()) {
      return false;
    }

    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.CONTROL_ROOM.DAILY_OPERATING_PLANS.ENTRIES
          .EDIT_ALL_DAILY_OPERATING_PLAN_ENTRIES
      )
    ) {
      return true;
    }

    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.CONTROL_ROOM.DAILY_OPERATING_PLANS.ENTRIES
          .EDIT_OWN_DAILY_OPERATING_PLAN_ENTRIES
      ) &&
      this.isOwnEntry(entry)
    ) {
      return true;
    }

    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.CONTROL_ROOM.DAILY_OPERATING_PLANS.ENTRIES
          .EDIT_DIRECT_REPORTS_DAILY_OPERATING_PLAN_ENTRIES
      )
    ) {
      return this.userReportsNames.some((name) =>
        entry.createdBy.toLowerCase().startsWith(name)
      );
    }

    return false;
  }

  canDeleteEntry(entry: DOPEntry): boolean {
    if (this.areEntriesReadOnly()) {
      return false;
    }

    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.CONTROL_ROOM.DAILY_OPERATING_PLANS.ENTRIES
          .DELETE_ALL_DAILY_OPERATING_PLAN_ENTRIES
      )
    ) {
      return true;
    }

    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.CONTROL_ROOM.DAILY_OPERATING_PLANS.ENTRIES
          .DELETE_OWN_DAILY_OPERATING_PLAN_ENTRIES
      ) &&
      this.isOwnEntry(entry)
    ) {
      return true;
    }

    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.CONTROL_ROOM.DAILY_OPERATING_PLANS.ENTRIES
          .DELETE_DIRECT_REPORTS_DAILY_OPERATING_PLAN_ENTRIES
      )
    ) {
      return this.userReportsNames.some((name) =>
        entry.createdBy.toLowerCase().startsWith(name)
      );
    }

    return false;
  }

  isOwnEntry(entry: DOPEntry): boolean {
    return this._auth.isOwnItem(entry.createdBy);
  }

  deleteEntry(entry: DOPEntry): void {
    let confHeader, ok, cancel, message: string;
    const confHeader$ = this._translateService.get(
      'DOP.MESSAGES.HEADERS.DELETE_ENTRY'
    );
    const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
    const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
    const message$ = this._translateService.get(
      'DOP.MESSAGES.CONFIRMATION.DELETE_ENTRY'
    );

    forkJoin([confHeader$, ok$, cancel$, message$]).subscribe((messages) => {
      confHeader = messages[0];
      ok = messages[1];
      cancel = messages[2];
      message = messages[3];
    });

    this._confirmationService.confirm({
      header: confHeader,
      icon: 'fa fa-question-circle',
      acceptVisible: true,
      rejectVisible: true,
      acceptLabel: ok,
      rejectLabel: cancel,
      message: message!,
      accept: () => {
        this.isLoading = true;
        this._dopApi.deleteDOPEntry(entry.id).subscribe(
          (result) => {
            this.isLoading = false;
            this.reload();
            this._logAndMessage.translateToSuccessMessage({
              headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
              bodyKey: 'DOP.MESSAGES.SUCCESS.DELETE_ENTRY',
            });
          },
          (error) => {
            this.isLoading = false;
            console.error(error);
            this._logAndMessage.translateToErrorAlert({
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
              bodyKey: 'DOP.MESSAGES.ERROR.DELETE_ENTRY',
            });
          }
        );
      },
      reject: () => {},
    });
  }
}
